import { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { localeFromPath, buildLocalizedPath, canonicalUrlFor, stripLocalePrefix, FR_INDEXABLE_PATHS } from '@/lib/locale-url';

const SITE_ORIGIN = 'https://www.brainfyai.com';

/**
 * Keeps the document, i18n state, and SEO meta in sync with the URL.
 *
 * The URL prefix is the source of truth for locale:
 *   - `/fr`, `/fr/*`  → locale 'fr-CA'
 *   - everything else → locale 'en-CA'
 *
 * On every route change this:
 *   1. Updates i18n.language so `t(...)` calls render the right locale.
 *   2. Updates <html lang> and data-locale.
 *   3. Emits a fallback canonical pointing at the English URL (see policy below).
 *   4. Emits og:locale + og:locale:alternate aligned with the URL.
 */
const LocaleSync = () => {
  const { i18n } = useTranslation();
  const location = useLocation();
  const urlLocale = localeFromPath(location.pathname);

  useEffect(() => {
    if (i18n.language !== urlLocale) {
      i18n.changeLanguage(urlLocale);
    }
  }, [urlLocale, i18n]);

  useEffect(() => {
    if (typeof document === 'undefined') return;
    document.documentElement.setAttribute('lang', urlLocale);
    document.documentElement.setAttribute('data-locale', urlLocale);
  }, [urlLocale]);

  // Indexing policy: the French routes mirror the English pages. Pages in FR_INDEXABLE_PATHS are
  // genuinely translated: they are self-canonical in each language and emit an hreflang pair.
  // Every other /fr/* URL is a UI-language convenience that canonicalizes to the English URL and
  // emits no hreflang (hreflang must point at self-canonical, equivalent pages).
  // Page-level canonicals override this fallback; it covers pages that set none.
  const englishUrl = `${SITE_ORIGIN}${buildLocalizedPath('en-CA', location.pathname)}`;
  const canonical = canonicalUrlFor(location.pathname);
  const hasFrench = FR_INDEXABLE_PATHS.includes(stripLocalePrefix(location.pathname));
  const frenchUrl = `${SITE_ORIGIN}${buildLocalizedPath('fr-CA', location.pathname)}`;
  const altLocale = urlLocale === 'fr-CA' ? 'en-CA' : 'fr-CA';

  return (
    <Helmet>
      <html lang={urlLocale} />
      <link rel="canonical" href={canonical} />
      {hasFrench && <link rel="alternate" hrefLang="en-CA" href={englishUrl} />}
      {hasFrench && <link rel="alternate" hrefLang="fr-CA" href={frenchUrl} />}
      {hasFrench && <link rel="alternate" hrefLang="x-default" href={englishUrl} />}
      {/* Default indexing directive; a page's own robots meta (noindex, SEOHead) replaces it. */}
      <meta name="robots" content="index, follow, max-image-preview:large" />
      <meta property="og:locale" content={urlLocale.replace('-', '_')} />
      <meta property="og:locale:alternate" content={altLocale.replace('-', '_')} />
    </Helmet>
  );
};

export default LocaleSync;
